import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
} from '@/components/ui'

import { formatCount, formatShortDate } from '../format'
import { useUsage, useUsageSummary } from '../queries'

/**
 * Card "Interações de IA" do Dashboard (`04` §4.3): saldo, fim do teste e barra consumido/total (a barra só para
 * quem tem `billing.view`). Agente pausado → action state com o caminho para Planos e uso.
 */
export function CreditsDashboardCard({
  tenantId,
  tenantSlug,
}: {
  readonly tenantId: string
  readonly tenantSlug: string
}) {
  const { t, i18n } = useTranslation()
  const summaryQuery = useUsageSummary(tenantId)
  const usageQuery = useUsage(tenantId, 30)
  const summary = summaryQuery.data
  const usage = usageQuery.data ?? null

  const credits = usage?.buckets.reduce((sum, b) => sum + b.credits, 0) ?? 0
  const consumed = usage?.buckets.reduce((sum, b) => sum + b.consumed, 0) ?? 0
  const percent = credits > 0 ? Math.min(100, Math.round((consumed / credits) * 100)) : 0

  return (
    <Card data-testid="credits-card">
      <CardHeader>
        <CardTitle>{t('billing.dashboard.title')}</CardTitle>
        {summary !== undefined && summary.trialEndsAt !== null && !summary.pausedForCredits ? (
          <CardDescription>
            {t('billing.dashboard.trialEndsAt', {
              date: formatShortDate(summary.trialEndsAt, i18n.language),
            })}
          </CardDescription>
        ) : null}
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm">
        {summaryQuery.isError ? (
          <Alert variant="danger" title={t('billing.dashboard.loadError')} />
        ) : summary === undefined ? (
          <Skeleton className="h-5 w-32" />
        ) : summary.pausedForCredits ? (
          <Alert variant="danger" title={t('billing.dashboard.paused')}>
            <Link
              to="/app/$tenantSlug/billing"
              params={{ tenantSlug }}
              className="font-medium text-action-primary hover:underline"
            >
              {t('billing.dashboard.viewPlans')}
            </Link>
          </Alert>
        ) : (
          <>
            <p className="text-2xl font-semibold text-foreground-primary">
              {t('billing.dashboard.available', {
                value: formatCount(Math.max(0, summary.available), i18n.language),
              })}
            </p>
            {credits > 0 ? (
              <div className="flex flex-col gap-1">
                <div
                  className="h-2 w-full overflow-hidden rounded-full bg-background-subtle"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={percent}
                  aria-label={t('billing.dashboard.consumedOf', {
                    consumed: formatCount(consumed, i18n.language),
                    credits: formatCount(credits, i18n.language),
                  })}
                >
                  <div
                    className={
                      summary.lowBalance ? 'h-full bg-status-warning' : 'h-full bg-action-primary'
                    }
                    style={{ width: `${String(percent)}%` }}
                  />
                </div>
                <span className="text-xs text-foreground-muted">
                  {t('billing.dashboard.consumedOf', {
                    consumed: formatCount(consumed, i18n.language),
                    credits: formatCount(credits, i18n.language),
                  })}
                </span>
              </div>
            ) : null}
            <Link
              to="/app/$tenantSlug/billing"
              params={{ tenantSlug }}
              className="text-sm font-medium text-action-primary hover:underline"
            >
              {t('billing.dashboard.viewUsage')}
            </Link>
          </>
        )}
      </CardContent>
    </Card>
  )
}
